import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./creator.css";
import { api, getToken, login, setToken } from "./api";
import { CreatorBar, CreatorFooter } from "./CreatorLanding";
import { loadTikTokPixel, ttTrack } from "./tiktok";
import { TokenVerify } from "../redux/actions/User";

const AUDIENCE = ["Under 1k", "1k–10k", "10k–50k", "50k–250k", "250k–1M", "1M+"];
const CONTENT = ["Music", "Lifestyle", "Nightlife", "Comedy", "Sports", "College life", "Food & drink", "Fashion", "Promoter / events"];
const EVENT_TYPES = ["Hip-hop", "R&B", "Pop", "Latin", "EDM", "Comedy", "Sports", "Festivals", "Theater"];

function Chips({ options, value, onChange, multiple, label }) {
  const toggle = (o) => {
    if (multiple) onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
    else onChange(value === o ? "" : o);
  };
  return (
    <div className="cp-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o} type="button" className="cp-chip" aria-pressed={multiple ? value.includes(o) : value === o} onClick={() => toggle(o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function CreatorApply() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [signedIn, setSignedIn] = useState(Boolean(getToken()));
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    tiktok: "",
    instagram: "",
    city: "",
    audienceSize: "",
    contentCategory: "",
    preferredEventCategories: [],
    password: "",
    agreeTerms: false,
    agreeDisclosure: false,
    marketingConsent: true,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");
  const [started, setStarted] = useState(false);
  const account = useSelector((state) => state.user.user);

  // One InstaPass account: applying uses the account you're signed in with.
  useEffect(() => {
    if (!signedIn || !account?.email) return;
    setForm((f) => ({
      ...f,
      fullName: f.fullName || [account.firstName, account.lastName].filter(Boolean).join(" "),
      email: account.email,
      phone: f.phone || account.phoneNumber || "",
    }));
  }, [signedIn, account?.email, account?.firstName, account?.lastName, account?.phoneNumber]);

  useEffect(() => {
    document.title = "Apply — InstaPass Creator Partners";
    loadTikTokPixel();
    // Already a creator? Go straight to the dashboard.
    if (getToken()) {
      api("/me")
        .then(() => navigate("/creators/dashboard", { replace: true }))
        .catch(() => {});
    }
  }, [navigate]);

  const set = (k) => (v) => {
    if (!started) {
      setStarted(true);
      ttTrack("ClickButton", { description: "application_started" });
    }
    setForm((f) => ({ ...f, [k]: v }));
  };
  const field = (k) => ({ value: form[k], onChange: (e) => set(k)(e.target.value) });

  async function submit(e) {
    e?.preventDefault();
    setError("");
    setBusy(true);
    let utm;
    try {
      utm = JSON.parse(sessionStorage.getItem("instapass_creator_utm") || "null") || undefined;
    } catch {}
    try {
      const res = await api("/apply", {
        method: "POST",
        body: { ...form, source: utm?.utm_source === "tiktok" || utm?.ttclid ? "tiktok_ad" : "organic", campaign: utm?.utm_campaign, utm },
      });
      if (res.token) setToken(res.token);
      dispatch(TokenVerify());
      ttTrack("SubmitForm");
      ttTrack("CompleteRegistration");
      navigate("/creators/dashboard?welcome=1", { replace: true });
    } catch (err) {
      if (err.code === "LOGIN_REQUIRED") {
        setNeedLogin(true);
        setError("");
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function signInAndApply(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(form.email, loginPassword);
      setSignedIn(true);
      setNeedLogin(false);
      dispatch(TokenVerify());
      await submit();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <div className="cp">
      <CreatorBar
        right={
          <Link to="/creators" className="cp-link">
            Program details
          </Link>
        }
      />
      <main className="cp-wrap" style={{ paddingTop: 36, paddingBottom: 72 }}>
        <h1 className="display" style={{ fontSize: "clamp(30px, 7vw, 56px)", margin: "0 0 12px" }}>
          Become a Creator Partner
        </h1>
        <p className="cp-lede" style={{ marginBottom: 32 }}>
          Two minutes. Our team reviews every application, and you'll get an email the moment you're approved.
        </p>

        {!signedIn ? (
          <div className="cp-form">
            <div className="cp-note">
              Creator Partners runs on your InstaPass account — the same login you use for tickets and hosting. Sign in, or create
              your free account, and you'll come right back here to finish.
            </div>
            <div className="cp-actions">
              <Link className="cp-btn" to="/login?next=%2Fcreators%2Fapply" onClick={() => ttTrack("ClickButton", { description: "apply_sign_in" })}>
                Sign in to apply
              </Link>
              <Link className="cp-btn ghost" to="/registration?next=%2Fcreators%2Fapply" onClick={() => ttTrack("ClickButton", { description: "apply_create_account" })}>
                Create your account
              </Link>
            </div>
          </div>
        ) : needLogin ? (
          <form className="cp-form" onSubmit={signInAndApply}>
            <div className="cp-note">
              {form.email} already has an InstaPass account. Sign in and we'll attach your Creator Partner application to it.
            </div>
            <div className="cp-field">
              <label htmlFor="lp">InstaPass password</label>
              <input id="lp" className="cp-input" type="password" autoComplete="current-password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
            </div>
            {error && <div className="cp-error" role="alert">{error}</div>}
            <div className="cp-actions">
              <button className="cp-btn" disabled={busy}>
                {busy ? "Signing in…" : "Sign in and submit"}
              </button>
              <button type="button" className="cp-link" onClick={() => setNeedLogin(false)}>
                Use a different email
              </button>
            </div>
            <Link to="/forgotpassword" className="cp-link">
              Forgot your password?
            </Link>
          </form>
        ) : (
          <form className="cp-form" onSubmit={submit} noValidate>
            <div className="cp-note">
              Applying as <strong>{form.fullName || "you"}</strong> ({form.email}). Your creator dashboard will live in this same account.
            </div>
            <div className="cp-field">
              <label htmlFor="ph">Phone</label>
              <input id="ph" className="cp-input" type="tel" autoComplete="tel" inputMode="tel" {...field("phone")} required />
            </div>
            <div className="cp-two">
              <div className="cp-field">
                <label htmlFor="tt">TikTok username</label>
                <input id="tt" className="cp-input" placeholder="@" autoCapitalize="none" {...field("tiktok")} />
              </div>
              <div className="cp-field">
                <label htmlFor="ig">Instagram username</label>
                <input id="ig" className="cp-input" placeholder="@" autoCapitalize="none" {...field("instagram")} />
              </div>
            </div>
            <div className="cp-field">
              <label htmlFor="city">Primary city</label>
              <input id="city" className="cp-input" autoComplete="address-level2" placeholder="Los Angeles" {...field("city")} required />
            </div>
            <div className="cp-field">
              <label>Audience size</label>
              <span className="help">Your biggest platform</span>
              <Chips label="Audience size" options={AUDIENCE} value={form.audienceSize} onChange={set("audienceSize")} />
            </div>
            <div className="cp-field">
              <label>What do you make content about?</label>
              <Chips label="Content category" options={CONTENT} value={form.contentCategory} onChange={set("contentCategory")} />
            </div>
            <div className="cp-field">
              <label>Events your audience shows up for</label>
              <span className="help">Pick any</span>
              <Chips label="Preferred event categories" multiple options={EVENT_TYPES} value={form.preferredEventCategories} onChange={set("preferredEventCategories")} />
            </div>
            {!signedIn && (
              <div className="cp-field">
                <label htmlFor="pw">Create a password</label>
                <span className="help">This becomes your InstaPass login. At least 8 characters.</span>
                <input id="pw" className="cp-input" type="password" autoComplete="new-password" {...field("password")} required minLength={8} />
              </div>
            )}
            <label className="cp-check">
              <input type="checkbox" checked={form.agreeTerms} onChange={(e) => set("agreeTerms")(e.target.checked)} />
              <span>
                I agree to the Creator Partner terms: commissions are earned only on verified, qualifying purchases through my links,
                aren't guaranteed, and exclude my own purchases, refunds and cancellations. I also agree to the InstaPass{" "}
                <Link to="/terms&conditions" target="_blank">terms</Link> and <Link to="/privacyPolicy" target="_blank">privacy policy</Link>.
              </span>
            </label>
            <label className="cp-check">
              <input type="checkbox" checked={form.agreeDisclosure} onChange={(e) => set("agreeDisclosure")(e.target.checked)} />
              <span>I'll clearly disclose that I earn a commission whenever I promote InstaPass events (for example, #ad).</span>
            </label>
            <label className="cp-check">
              <input type="checkbox" checked={form.marketingConsent} onChange={(e) => set("marketingConsent")(e.target.checked)} />
              <span>Send me program emails — new events, tips and performance updates. Unsubscribe anytime.</span>
            </label>
            {error && (
              <div className="cp-error" role="alert">
                {error}
              </div>
            )}
            <div className="cp-actions">
              <button className="cp-btn" disabled={busy}>
                {busy ? "Submitting…" : "Submit application"}
              </button>
              {!signedIn && (
                <button type="button" className="cp-link" onClick={() => (form.email ? setNeedLogin(true) : setError("Enter your email above, then sign in."))}>
                  Have an InstaPass account? Sign in
                </button>
              )}
            </div>
          </form>
        )}
      </main>
      <CreatorFooter />
    </div>
  );
}
